/**
 * ScenarioAuditLogTab
 *
 * A scenario's audit trail as a vertical timeline, most recent first. Live mode
 * reads vw_ScenarioAuditLog through GraphQL; demo mode passes the in-memory
 * entries built by `appendAuditEntry()`. Each entry shows a colour-coded icon
 * for its audit type, the title and description, the changed fields as
 * old → new badges, and who did it.
 */

import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  History,
  Link2,
  PauseCircle,
  RefreshCw,
  Send,
  SquarePen,
  Undo2,
  User,
  Users,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { ScenarioAuditLogEntry } from "../../api/models/scenarioAuditLogView";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

type DateFilter = "today" | "7d" | "30d";

const DATE_FILTERS: { value: DateFilter; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

/** Icon + badge colour per ScenarioAuditLog.AuditType. */
const AUDIT_TYPE_STYLES: Record<
  string,
  { icon: typeof History; className: string }
> = {
  System: { icon: History, className: "bg-gray-100 text-gray-600" },
  Adjustment: { icon: SquarePen, className: "bg-cyan-100 text-cyan-700" },
  Parked: { icon: PauseCircle, className: "bg-amber-100 text-amber-700" },
  Approved: { icon: CheckCircle2, className: "bg-green-100 text-green-700" },
  SentForReview: { icon: Send, className: "bg-blue-100 text-blue-700" },
  SentForApproval: { icon: Send, className: "bg-purple-100 text-purple-700" },
  Withdrawn: { icon: Undo2, className: "bg-slate-100 text-slate-700" },
  Rejected: { icon: XCircle, className: "bg-red-100 text-red-700" },
  Refreshed: { icon: RefreshCw, className: "bg-indigo-100 text-indigo-700" },
  Shared: { icon: Users, className: "bg-teal-100 text-teal-700" },
  Issue: { icon: AlertTriangle, className: "bg-orange-100 text-orange-700" },
  Dependency: { icon: Link2, className: "bg-violet-100 text-violet-700" },
};

const DEFAULT_AUDIT_TYPE_STYLE = {
  icon: History,
  className: "bg-gray-100 text-gray-600",
};

interface ScenarioAuditLogTabProps {
  entries: ScenarioAuditLogEntry[];
  isLoading?: boolean;
}

function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Turn PascalCase field names from the audit JSON into readable labels. */
function formatFieldName(field: string): string {
  return field.replace(/([a-z])([A-Z])/g, "$1 $2");
}

/** Union of the field names present on either side of the change. */
function changedFields(entry: ScenarioAuditLogEntry): string[] {
  return Array.from(
    new Set([
      ...Object.keys(entry.oldValues ?? {}),
      ...Object.keys(entry.newValues ?? {}),
    ]),
  );
}

const AuditTimelineEntry: React.FC<{
  entry: ScenarioAuditLogEntry;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { icon: Icon, className } =
    AUDIT_TYPE_STYLES[entry.auditType] ?? DEFAULT_AUDIT_TYPE_STYLE;
  const fields = changedFields(entry);

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${className}`}
        >
          <Icon className="h-4 w-4" />
        </div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
      </div>

      <div className={`min-w-0 flex-1 ${isLast ? "" : "pb-5"}`}>
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-foreground">{entry.title}</p>
          <span className="shrink-0 text-xs text-muted-foreground">
            {format(entry.timestamp, "d MMM yyyy, HH:mm")}
          </span>
        </div>

        {entry.description && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            {entry.description}
          </p>
        )}

        {/* Changed fields flow inline rather than one per row, so a create event
				    carrying a dozen fields stays a couple of lines instead of a long list. */}
        {fields.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {fields.map((field) => {
              const hasOld = entry.oldValues && field in entry.oldValues;
              const hasNew = entry.newValues && field in entry.newValues;
              return (
                <span key={field} className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {formatFieldName(field)}
                  </span>
                  {hasOld && (
                    <Badge variant="outline" className="font-normal">
                      {formatAuditValue(entry.oldValues?.[field])}
                    </Badge>
                  )}
                  {hasOld && hasNew && (
                    <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  {hasNew && (
                    <Badge variant="outline" className="font-normal">
                      {formatAuditValue(entry.newValues?.[field])}
                    </Badge>
                  )}
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          {entry.user ?? "System"}
        </div>
      </div>
    </div>
  );
};

export const ScenarioAuditLogTab: React.FC<ScenarioAuditLogTabProps> = ({
  entries,
  isLoading = false,
}) => {
  const [dateFilter, setDateFilter] = useState<DateFilter>("30d");

  const visibleEntries = useMemo(() => {
    const cutoff = new Date();
    if (dateFilter === "today") {
      cutoff.setHours(0, 0, 0, 0);
    } else {
      cutoff.setDate(cutoff.getDate() - (dateFilter === "7d" ? 7 : 30));
    }

    return entries
      .filter((entry) => entry.timestamp >= cutoff)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [entries, dateFilter]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4" />
          Audit Log
        </CardTitle>
        <div className="flex gap-1">
          {DATE_FILTERS.map((option) => {
            const isSelected = option.value === dateFilter;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setDateFilter(option.value)}
                className={`rounded-md border px-3 py-1 text-xs transition-colors ${
                  isSelected
                    ? "border-[var(--accent-teal-500)] bg-[var(--accent-teal-50)] font-medium text-teal-700"
                    : "border-gray-200 text-muted-foreground hover:bg-gray-50"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && visibleEntries.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-muted-foreground">No Data</p>
          </div>
        ) : (
          <div>
            {visibleEntries.map((entry, index) => (
              <AuditTimelineEntry
                key={entry.id}
                entry={entry}
                isLast={index === visibleEntries.length - 1}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
